import {
  getSampleAutoItemIDs,
  resolveSampleDescriptionItemIDs,
  toSamplesExplainedSources,
} from '@team-milva/lab-models/resources'
import type {
  IAnalysisResult,
  IReportDescription,
  IReportDescriptionItem,
  ISampleDescriptionOverride,
  ITextList,
} from '@team-milva/lab-models/resources'
import { getTextObjValue } from '@team-milva/lab-models/textObj'
import type { Analysis } from '@/globals/typescript/models/Analysis'
import type { AnalysisTest } from '@/globals/typescript/models/AnalysisTest'
import type { Order } from '@/globals/typescript/models/Order'
import { resolveOrderResultsForReport } from '@/globals/typescript/utils/resolveOrderResultsForReport'

export interface IReportSampleDescriptionState {
  sampleID: string
  sampleNumber: number
  /** Names of the analyses on the sample, alphabetically — shown on its card. */
  analysisNames: string[]
  /** Auto descriptions that appear for this sample anyway — shown read-only so they are not added twice. */
  autoDescriptions: { name: string; text: string }[]
  /** Eligible items picked on this sample's results, in results-table order. */
  autoItemIDs: string[]
  /** The sample's final ordered item IDs, with the overrides applied. */
  itemIDs: string[]
  /** Auto-matched items the user removed — shown so they can be restored. */
  removedAutoItemIDs: string[]
}

/**
 * Per-sample description state for one report group, for the Reports tab card and its modal.
 * Built on the same resolver the preview and PDF use, so the numbers shown here are the report's.
 *
 * `overridesBySampleID` defaults to what is saved on the order; the modal passes its draft instead.
 */
export function resolveReportSampleDescriptionStates({
  order,
  tatGroupID,
  reportTemplateID,
  results,
  allAnalyses,
  allAnalysisTests,
  systemLists,
  reportDescriptionItems,
  reportDescriptions,
  overridesBySampleID,
  labAccountID,
}: {
  order: Order
  tatGroupID: string
  reportTemplateID: string
  results: IAnalysisResult[]
  allAnalyses: Analysis[]
  allAnalysisTests: AnalysisTest[]
  systemLists: ITextList[]
  reportDescriptionItems: IReportDescriptionItem[]
  reportDescriptions: IReportDescription[]
  overridesBySampleID?: Record<string, ISampleDescriptionOverride>
  labAccountID: string
}): IReportSampleDescriptionState[] {
  const locale = order.reportLocale
  const overrides =
    overridesBySampleID ??
    order.reportSampleDescriptionOverrides?.[`${tatGroupID}__${reportTemplateID}`] ??
    {}

  const sources = toSamplesExplainedSources(
    resolveOrderResultsForReport({
      order,
      reportTemplateID,
      tatGroupID,
      results,
      allAnalyses,
      allAnalysisTests,
      allReportDescriptionItems: reportDescriptionItems,
      systemLists,
      locale,
    })
  )

  const itemsByID = new Map(
    reportDescriptionItems
      .filter((item) => item.labAccountID === labAccountID)
      .map((item) => [item.id, item])
  )
  const descriptionsByID = new Map(
    reportDescriptions.map((description) => [description.id, description])
  )

  return sources.map((source) => {
    const autoItemIDs = getSampleAutoItemIDs({
      sample: source,
      reportDescriptionItemsByID: itemsByID,
      reportDescriptionsByID: descriptionsByID,
      locale,
    })
    const { itemIDs, removedAutoItemIDs } = resolveSampleDescriptionItemIDs({
      autoItemIDs,
      override: overrides[source.sampleID],
    })

    const autoDescriptions: IReportSampleDescriptionState['autoDescriptions'] = []
    const analysisNames = new Set<string>()
    for (const sourceAnalysis of source.analyses) {
      const analysis = allAnalyses.find((x) => x.id === sourceAnalysis.analysisID)
      if (analysis) analysisNames.add(getTextObjValue(analysis.name, locale))
      if (!analysis?.autoDescription?.enabled) continue
      const text = getTextObjValue(analysis.autoDescription.text, locale)
      if (!text) continue
      autoDescriptions.push({ name: getTextObjValue(analysis.name, locale), text })
    }

    return {
      sampleID: source.sampleID,
      sampleNumber: source.sampleNumber,
      analysisNames: [...analysisNames].filter(Boolean).sort((a, b) => a.localeCompare(b)),
      autoDescriptions,
      autoItemIDs,
      itemIDs,
      removedAutoItemIDs,
    }
  })
}
